/**
 * The client headers the gateway records on every call, as the Requests
 * and Conversations tables show and filter them (TRA-1397): tenant,
 * environment, traffic, session and initiator. One definition, so the two
 * tables cannot drift into different columns or dropdowns for the same
 * five fields. `ClientMetaCell` renders one of these columns.
 *
 * Kept free of React so the decisions run in the node vitest setup.
 */

import { plainFilterValue, type MetadataFilterSpec } from './listingParams'

/** The five client headers as both listing rows carry them. */
export interface ClientMetadata {
  client_tenant_id?: string | null
  client_tenant_name?: string | null
  environment?: string | null
  traffic_type?: string | null
  session_id?: string | null
  initiated_by?: string | null
}

/** The tenant as the table shows it: the name when the client sent one. */
export function tenantLabel(item: ClientMetadata): string | null {
  return item.client_tenant_name ?? item.client_tenant_id ?? null
}

/** The client headers, one column each. */
export const CLIENT_META_COLUMNS: {
  label: string
  value: (item: ClientMetadata) => string | null
}[] = [
  { label: 'Tenant', value: tenantLabel },
  { label: 'Environment', value: (i) => i.environment ?? null },
  { label: 'Traffic', value: (i) => i.traffic_type ?? null },
  { label: 'Session', value: (i) => i.session_id ?? null },
  { label: 'Initiated by', value: (i) => i.initiated_by ?? null },
]

/** The dropdowns for the five client headers. The tenant filters on the
    id header and shows the name when the client sent one. */
export function clientMetadataFilters<Item extends ClientMetadata>(): MetadataFilterSpec<Item>[] {
  return [
    {
      param: 'tenant',
      label: 'Tenant',
      values: (i) =>
        i.client_tenant_id == null
          ? []
          : [{ value: i.client_tenant_id, label: i.client_tenant_name ?? i.client_tenant_id }],
    },
    { param: 'environment', label: 'Environment', values: (i) => plainFilterValue(i.environment) },
    { param: 'traffic', label: 'Traffic', values: (i) => plainFilterValue(i.traffic_type) },
    { param: 'session', label: 'Session', values: (i) => plainFilterValue(i.session_id) },
    { param: 'initiator', label: 'Initiated by', values: (i) => plainFilterValue(i.initiated_by) },
  ]
}
